import React, { useContext } from "react";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
} from "@mui/material";
import {
  Security as TSSIcon,
  Person as ClientIcon,
  Receipt as TransactionsIcon,
  FileDownload as ExportIcon,
  Dashboard as DashboardIcon,
} from "@mui/icons-material";
import { SettingsContext, SettingSection } from "./SettingsLayout";

interface MenuItemConfig {
  id: SettingSection;
  label: string;
  icon: React.ReactElement;
  description?: string;
}

const menuItems: MenuItemConfig[] = [
  {
    id: "Dashboard",
    label: "Dashboard",
    icon: <DashboardIcon />,
    description: "Overview & statistics",
  },
  {
    id: "TSS",
    label: "TSS Management",
    icon: <TSSIcon />,
    description: "Technical Security System",
  },
  {
    id: "Client",
    label: "Clients",
    icon: <ClientIcon />,
    description: "Client configuration",
  },
  {
    id: "Transactions",
    label: "Transactions",
    icon: <TransactionsIcon />,
    description: "Transaction history",
  },
  {
    id: "Export",
    label: "Exports",
    icon: <ExportIcon />,
    description: "Data export & backup",
  },
];

const SettingsSidebar: React.FC = React.memo(() => {
  const { activeSection, setActiveSection } = useContext(SettingsContext);

  const handleItemClick = React.useCallback(
    (section: SettingSection) => {
      setActiveSection(section);
    },
    [setActiveSection]
  );

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      {/* Header */}
      {/* <Box
        sx={{
          p: 2.5,
          bgcolor: "#8d9bd7",
          borderBottom: "3px solid #5a67d8",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: "#ffffff",
            fontSize: "1.25rem",
            letterSpacing: "0.5px",
          }}
        >
          ⚙️ Settings
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: "rgba(255, 255, 255, 0.85)",
            fontSize: "0.75rem",
            display: "block",
            mt: 0.5,
          }}
        >
          Manage your taxation system
        </Typography>
      </Box> */}

      {/* Menu Items */}
      <List sx={{ pt: 2, px: 1.5 }}>
        {menuItems.map((item) => (
          <ListItem key={item.id} disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              selected={activeSection === item.id}
              onClick={() => handleItemClick(item.id)}
              sx={{
                borderRadius: 2,
                py: 1.5,
                px: 2,
                transition: "all 0.3s ease",
                position: "relative",
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: "4px",
                  bgcolor:
                    activeSection === item.id ? "#003059" : "transparent",
                  transition: "all 0.3s ease",
                },
                "&.Mui-selected": {
                  bgcolor: "rgba(102, 126, 234, 0.12)",
                  boxShadow: "0 2px 8px rgba(102, 126, 234, 0.15)",
                  "&:hover": {
                    bgcolor: "rgba(102, 126, 234, 0.18)",
                  },
                  "& .MuiListItemIcon-root": {
                    color: "#003059",
                    transform: "scale(1.1)",
                  },
                  "& .MuiListItemText-primary": {
                    color: "#003059",
                    fontWeight: 600,
                  },
                },
                "&:hover": {
                  bgcolor: "rgba(102, 126, 234, 0.08)",
                  transform: "translateX(4px)",
                  "& .MuiListItemIcon-root": {
                    transform: "scale(1.05)",
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 40,
                  color: activeSection === item.id ? "#667eea" : "#64748b",
                  transition: "all 0.3s ease",
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                secondary={item.description}
                primaryTypographyProps={{
                  fontSize: "0.9rem",
                  fontWeight: activeSection === item.id ? 600 : 500,
                  color: activeSection === item.id ? "#667eea" : "#334155",
                }}
                secondaryTypographyProps={{
                  fontSize: "0.7rem",
                  sx: {
                    display: { xs: "none", sm: "block" },
                    color: activeSection === item.id ? "#4e515d" : "#94a3b8",
                    mt: 0.25,
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );
});

SettingsSidebar.displayName = "SettingsSidebar";

export default SettingsSidebar;
